import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";
import { LogOut, Package, ArrowLeft } from "lucide-react";
import { useCustomerAuth } from "../context/CustomerAuth";
import { inr } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const badge = {
  pending: "bg-[#C5A059]/20 text-[#7a5a20]",
  payment_pending: "bg-[#C5A059]/20 text-[#7a5a20]",
  payment_failed: "bg-red-800/10 text-red-800",
  confirmed: "bg-[#2E6669]/15 text-[#2E6669]",
  delivered: "bg-green-800/10 text-green-800",
  cancelled: "bg-red-800/10 text-red-800",
};

export default function AccountPage() {
  const location = useLocation();
  const { user, setUser, signIn, signOut } = useCustomerAuth();
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  const me = location.state?.user || user;

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      return;
    }
    if (user === undefined) return;
  }, [location.state, user, setUser]);

  useEffect(() => {
    if (!me) return;
    setLoadingOrders(true);
    axios
      .get(`${API}/auth/my-orders`, { withCredentials: true })
      .then((r) => setOrders(r.data))
      .catch(() => {})
      .finally(() => setLoadingOrders(false));
  }, [me]);

  return (
    <div className="min-h-screen bg-[#FBF9F6]" data-testid="account-page">
      <header className="bg-[#2D1B36] text-[#FBF9F6]">
        <div className="max-w-[900px] mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" data-testid="account-back-home" className="font-serif-d text-2xl">
            Innus <span className="italic text-[#C5A059]">Lace Centre</span>
          </Link>
          {me && (
            <button
              onClick={signOut}
              data-testid="account-signout-button"
              className="flex items-center gap-2 border border-[#FBF9F6]/30 px-4 py-2 font-accent text-[10px] tracking-[0.25em] uppercase hover:bg-[#FBF9F6]/10 transition-colors"
            >
              <LogOut size={12} /> Sign Out
            </button>
          )}
        </div>
      </header>

      <main className="max-w-[900px] mx-auto px-6 py-14">
        {!me ? (
          <div className="text-center py-24" data-testid="account-signin-prompt">
            <h1 className="font-serif-d font-light text-4xl text-[#2D1B36]">
              Your account, <span className="italic text-[#C5A059]">your orders</span>
            </h1>
            <p className="mt-4 text-[#514357] max-w-md mx-auto leading-relaxed">
              Sign in with Google, email or phone to track your orders and check
              out faster. Guest shopping still works — no account needed.
            </p>
            <Link
              to="/auth"
              data-testid="account-auth-link"
              className="mt-10 inline-flex items-center gap-3 bg-[#4A2B58] text-[#FBF9F6] font-accent text-xs tracking-[0.25em] uppercase px-8 py-4 hover:bg-[#2D1B36] transition-colors duration-300"
            >
              Sign In or Create Account
            </Link>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-5 pb-10 border-b border-[#E8E4DF]" data-testid="account-profile">
              {me.picture ? (
                <img src={me.picture} alt="" className="w-16 h-16 rounded-full object-cover border border-[#E8E4DF]" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-[#4A2B58] text-[#FBF9F6] flex items-center justify-center font-serif-d text-2xl">
                  {(me.name || "?")[0]}
                </div>
              )}
              <div>
                <h1 className="font-serif-d text-3xl text-[#2D1B36]" data-testid="account-name">{me.name}</h1>
                <p className="text-sm text-[#8A818F]" data-testid="account-email">{me.email}</p>
              </div>
            </div>

            <h2 className="mt-12 mb-6 font-accent text-xs tracking-[0.3em] uppercase text-[#8A818F] flex items-center gap-2">
              <Package size={14} /> My Orders
            </h2>
            {loadingOrders ? (
              <p className="font-serif-d italic text-xl text-[#8A818F]">Loading your orders…</p>
            ) : orders.length === 0 ? (
              <div data-testid="my-orders-empty" className="text-center py-16 border border-dashed border-[#E8E4DF]">
                <p className="font-serif-d italic text-xl text-[#8A818F]">No orders yet.</p>
                <Link
                  to="/"
                  data-testid="my-orders-shop-link"
                  className="mt-4 inline-flex items-center gap-2 font-accent text-[11px] tracking-[0.25em] uppercase text-[#4A2B58] hover:text-[#C5A059] transition-colors"
                >
                  <ArrowLeft size={13} /> Start shopping
                </Link>
              </div>
            ) : (
              <div className="space-y-4" data-testid="my-orders-list">
                {orders.map((o) => (
                  <article key={o.id} data-testid={`my-order-${o.id}`} className="bg-white border border-[#E8E4DF] p-6">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <h3 className="font-serif-d text-xl text-[#2D1B36]">#{o.id}</h3>
                        <span className={`font-accent text-[10px] tracking-[0.2em] uppercase px-3 py-1 ${badge[o.status] || badge.pending}`}>
                          {o.status}
                        </span>
                      </div>
                      <span className="text-xs text-[#8A818F]">
                        {o.created_at ? new Date(o.created_at).toLocaleString("en-IN") : ""}
                      </span>
                    </div>
                    <ul className="mt-4 space-y-1">
                      {(o.items || []).map((i, idx) => (
                        <li key={idx} className="text-sm text-[#514357] flex justify-between gap-4">
                          <span>{i.qty} × {i.name}</span>
                          <span>{inr(i.price * i.qty)}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="mt-4 pt-3 border-t border-[#E8E4DF] flex justify-between text-[#2D1B36]">
                      <span className="font-accent text-[10px] tracking-[0.25em] uppercase text-[#8A818F]">{o.payment || "Paid online"}</span>
                      <span className="font-serif-d text-lg">{inr(o.total)}</span>
                    </p>
                  </article>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
